import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/shared/components/form';
import { MAX_CONTEXT_BLOCKS } from '../../lib/editorState';
import { EditorSection } from './EditorSection';
import type { SectionProps } from './types';

export function ContextSection({ draft, dispatch, readOnly, errors }: SectionProps) {
  const { t } = useTranslation('crm');
  return (
    <EditorSection
      title={t('estimates.context.title')}
      actions={!readOnly && (
        <Button
          type="button" size="sm" variant="outline" disabled={draft.businessContext.length >= MAX_CONTEXT_BLOCKS}
          onClick={() => dispatch({ type: 'addContext' })}
        >
          <Plus className="h-4 w-4 mr-1" /> {t('estimates.context.add')}
        </Button>
      )}
    >
      {draft.businessContext.length === 0 && <p className="text-sm text-muted-foreground">{t('estimates.context.empty')}</p>}
      {draft.businessContext.map((block, index) => {
        const n = index + 1;
        return (
          <div key={index} className="space-y-2 rounded-md border p-3">
            <div className="flex items-start gap-2">
              <FormField
                label={t('estimates.context.heading', { n })} htmlFor={`est-context-${index}-heading`}
                error={errors[`businessContext[${index}].heading`]} className="flex-1"
              >
                <Input
                  id={`est-context-${index}-heading`} value={block.heading} maxLength={120} disabled={readOnly}
                  onChange={(e) => dispatch({ type: 'setContext', index, patch: { heading: e.target.value } })}
                />
              </FormField>
              {!readOnly && (
                <Button
                  type="button" variant="ghost" size="icon" className="mt-6" aria-label={t('estimates.context.remove', { n })}
                  onClick={() => dispatch({ type: 'removeContext', index })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <FormField label={t('estimates.context.body', { n })} htmlFor={`est-context-${index}-body`} error={errors[`businessContext[${index}].body`]}>
              <Textarea
                id={`est-context-${index}-body`} rows={3} value={block.body} maxLength={1200} disabled={readOnly}
                onChange={(e) => dispatch({ type: 'setContext', index, patch: { body: e.target.value } })}
              />
            </FormField>
          </div>
        );
      })}
    </EditorSection>
  );
}
